/**
 * Pure arithmetic behind the calendar screens -- no fetch, no clock read, no
 * React. Kept apart from the components so "which day does this event fall
 * in", "which column it shares with an overlapping one", "how tall does it
 * draw" and "what does an empty free/busy answer mean" can be tested
 * without rendering anything (`layout.test.ts`).
 *
 * The week grid draws its own event blocks (`Week.tsx`, matching the
 * board's `.ev` markup and tiers directly -- `EventBlock` has no hook for a
 * calendar's own colour, and the board's fill and left edge are exactly
 * that colour). `FreeBusy` still draws its own bands from
 * `freeBusyBands`'s output below.
 */
import type { AvailabilityPeriod, CalendarEvent, CalendarRange } from '@wtfalch/calendar/calendar';
import type { FreeBusySlot, FreeBusyStatus } from '@wtfalch/design';

/** Local midnight on the Monday of `date`'s week. A week is what a person
 *  works in (Monday first), not what a calendar library defaults to
 *  (Sunday first). */
export function startOfWeek(date: Date): Date {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const sinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - sinceMonday);
  return d;
}

/** The seven local dates of the week starting at `start` (a Monday). */
export function weekDays(start: Date): Date[] {
  return Array.from(
    { length: 7 },
    (_, i) => new Date(start.getFullYear(), start.getMonth(), start.getDate() + i),
  );
}

/** The bound `listEvents` needs for a week starting at `start`: everything
 *  from that Monday's midnight up to (not including) the Monday after. */
export function weekRange(start: Date): CalendarRange {
  const before = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 7);
  return { after: start.toISOString(), before: before.toISOString() };
}

/** Which column of `days` an event's master start falls on, or `null` when
 *  it falls outside the week entirely (a defensive case: `listEvents`
 *  should never hand back an event outside the range it was asked for). */
export function dayIndex(
  event: Pick<CalendarEvent, 'start'>,
  days: readonly Date[],
): number | null {
  const start = new Date(event.start);
  const index = days.findIndex(
    (d) =>
      d.getFullYear() === start.getFullYear() &&
      d.getMonth() === start.getMonth() &&
      d.getDate() === start.getDate(),
  );
  return index === -1 ? null : index;
}

/** How many lines of text an event's block has room for, the board's own
 *  three tiers (`pages_agora.py`'s `week()`): thirty minutes or less is one
 *  line, under ninety is two, ninety or more earns the third -- a "where"
 *  or the calendar's own name. */
export type EventTier = 'tiny' | 'one' | 'tall';

export function eventTier(durationMinutes: number): EventTier {
  if (durationMinutes >= 90) return 'tall';
  if (durationMinutes > 30) return 'one';
  return 'tiny';
}

/** One hour, in pixels -- the board's own `.hour{height:56px}`, and the
 *  scale every event box in the week grid is measured from. */
export const HOUR_PX = 56;

/** An event's pixel box within its day column, measured from `hours.start`.
 *  Clamped to at least 24px tall, the same floor the board's own
 *  `max(length*56-4, 24)` keeps so a five-minute block is still a block. */
export function eventBox(
  start: Date,
  end: Date,
  hoursStart: number,
): { top: number; height: number } {
  const startHour = start.getHours() + start.getMinutes() / 60;
  const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
  return {
    top: (startHour - hoursStart) * HOUR_PX,
    height: Math.max(durationHours * HOUR_PX - 4, 24),
  };
}

/** The hours the week grid draws: `base`, widened to take in every timed
 *  event of the week -- an event outside the working day would otherwise
 *  paint above the day header or be cut off below the last row. All-day
 *  events have their own row and do not count. A timed event that runs past
 *  midnight is drawn to the end of its start day. */
export function weekHours(
  events: readonly Pick<CalendarEvent, 'start' | 'duration' | 'isAllDay'>[],
  base: { start: number; end: number },
): { start: number; end: number } {
  let { start, end } = base;
  for (const e of events) {
    if (e.isAllDay) continue;
    const from = new Date(e.start);
    const to = new Date(from.getTime() + durationMinutes(e.duration) * 60_000);
    start = Math.min(start, from.getHours());
    const sameDay = to.getDate() === from.getDate() && to.getMonth() === from.getMonth();
    end = Math.max(end, sameDay ? Math.ceil(to.getHours() + to.getMinutes() / 60) : 24);
  }
  return { start, end };
}

/** An event's duration in minutes, from an ISO 8601 duration like `PT1H` or
 *  `PT30M`. Only the units `CalendarEventDraft.duration` actually uses. */
export function durationMinutes(iso: string): number {
  const match = /^PT(?:(\d+)H)?(?:(\d+)M)?$/.exec(iso);
  if (!match) return 0;
  const hours = match[1] ? Number(match[1]) : 0;
  const minutes = match[2] ? Number(match[2]) : 0;
  return hours * 60 + minutes;
}

export function toIsoDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours && mins) return `PT${hours}H${mins}M`;
  if (hours) return `PT${hours}H`;
  return `PT${mins}M`;
}

/** Clip every period to the span, drop anything that lands entirely outside
 *  it, then merge overlaps so two periods that touch become one busy band
 *  rather than a hairline "free" sliver between them. Shared by
 *  `freeBusyBands` and `ownFreeBusyBands` below -- the only place the two
 *  differ is what an empty `periods` list means. */
function mergeBusyPeriods(
  periods: readonly { start: string; end: string }[],
  spanStart: number,
  spanEnd: number,
): { start: number; end: number }[] {
  return periods
    .map((p) => ({
      start: Math.max(new Date(p.start).getTime(), spanStart),
      end: Math.min(new Date(p.end).getTime(), spanEnd),
    }))
    .filter((p) => p.end > p.start)
    .sort((a, b) => a.start - b.start)
    .reduce<{ start: number; end: number }[]>((merged, next) => {
      const last = merged[merged.length - 1];
      if (last && next.start <= last.end) {
        last.end = Math.max(last.end, next.end);
      } else {
        merged.push({ ...next });
      }
      return merged;
    }, []);
}

/** The merged busy periods, with the gaps between (and around) them filled
 *  in as free. */
function fillFree(
  busy: readonly { start: number; end: number }[],
  spanStart: number,
  spanEnd: number,
): FreeBusySlot[] {
  const slots: FreeBusySlot[] = [];
  let cursor = spanStart;
  for (const period of busy) {
    if (period.start > cursor) {
      slots.push({ status: 'free', span: period.start - cursor });
    }
    slots.push({ status: 'busy', span: period.end - period.start });
    cursor = period.end;
  }
  if (cursor < spanEnd) {
    slots.push({ status: 'free', span: spanEnd - cursor });
  }
  return slots;
}

/**
 * `AvailabilityPeriod[]` for one person over one span, turned into the
 * ordered `FreeBusySlot[]` `FreeBusy` draws.
 *
 * **An empty answer is never "free."** `AvailabilityPeriod`'s own doc
 * comment: never-shared, shared-then-revoked and genuinely-free all come
 * back as nothing, and Stalwart has no way to tell them apart. So an empty
 * `periods` draws one hatched `unknown` band across the whole span --
 * "can't tell," never "free" -- which is the one rule this function exists
 * to make impossible to get wrong (`layout.test.ts`'s
 * "unshared, never free"). For your *own* row, which is never "not shared
 * with you", use `ownFreeBusyBands` instead.
 */
export function freeBusyBands(
  periods: readonly AvailabilityPeriod[],
  span: { start: Date; end: Date },
): FreeBusySlot[] {
  const spanStart = span.start.getTime();
  const spanEnd = span.end.getTime();
  if (spanEnd <= spanStart) return [];
  if (periods.length === 0) {
    return [{ status: 'unknown', span: spanEnd - spanStart }];
  }
  return fillFree(mergeBusyPeriods(periods, spanStart, spanEnd), spanStart, spanEnd);
}

/**
 * The same arithmetic as `freeBusyBands`, without its "empty means unknown"
 * rule: your own calendar can never be "not shared with you", so a day with
 * no events on it is a free day, not an ambiguous one. Built from whatever
 * periods the caller already has (`FindTime`'s own busy periods, taken
 * straight from the day's events) rather than a `getAvailability` call on
 * yourself.
 */
export function ownFreeBusyBands(
  periods: readonly { start: string; end: string }[],
  span: { start: Date; end: Date },
): FreeBusySlot[] {
  const spanStart = span.start.getTime();
  const spanEnd = span.end.getTime();
  if (spanEnd <= spanStart) return [];
  const slots = fillFree(mergeBusyPeriods(periods, spanStart, spanEnd), spanStart, spanEnd);
  return slots.length > 0 ? slots : [{ status: 'free', span: spanEnd - spanStart }];
}

/** Which band a moment falls in, or `null` outside the span -- used to read
 *  back "is everyone free at the time I just picked" from the same bands
 *  `FreeBusy` draws, rather than a second call to work it out. */
export function statusAt(
  bands: readonly FreeBusySlot[],
  span: { start: Date; end: Date },
  at: Date,
): FreeBusyStatus | null {
  const atMs = at.getTime();
  if (atMs < span.start.getTime() || atMs >= span.end.getTime()) return null;
  let cursor = span.start.getTime();
  for (const band of bands) {
    const next = cursor + band.span;
    if (atMs < next) return band.status;
    cursor = next;
  }
  return null;
}

const WEEKDAY_DATE = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});
const DAY_MONTH = new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'long' });
const TIME = new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' });

/** "21 – 27 September", or "28 September – 3 October" across a month. */
export function weekRangeLabel(days: readonly Date[]): string {
  const first = days[0];
  const last = days[days.length - 1];
  if (!first || !last) return '';
  const sameMonth =
    first.getMonth() === last.getMonth() && first.getFullYear() === last.getFullYear();
  const firstLabel = sameMonth ? String(first.getDate()) : DAY_MONTH.format(first);
  return `${firstLabel} – ${DAY_MONTH.format(last)}`;
}

/** "Thursday 24 September · 10:00 – 11:00". */
export function eventTimeLabel(start: Date, end: Date): string {
  return `${WEEKDAY_DATE.format(start)} · ${TIME.format(start)} – ${TIME.format(end)}`;
}

export function formatDuration(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  const parts: string[] = [];
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (mins) parts.push(`${mins} minute${mins === 1 ? '' : 's'}`);
  return parts.length ? parts.join(' ') : '0 minutes';
}
